import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import type {
  AdminRegenerateKeyResponse,
  AdminTenantDetailResponse,
} from '../api/client';
import { useAdminAuth } from '../auth/AuthProvider';
import { ConfirmModal } from '../components/ConfirmModal';
import { MaskedSecret } from '../components/MaskedSecret';
import { TenantStatusBadge } from '../components/StatusBadge';
import { formatDate } from '../lib/format';

export function TenantDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { fetcher } = useAdminAuth();
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'tenants', id],
    queryFn: () => fetcher<AdminTenantDetailResponse>(`/admin/api/tenants/${id}`),
    enabled: !!id,
  });

  const [name, setName] = useState<string>('');
  const [quota, setQuota] = useState<string>('');
  const [confirmSuspend, setConfirmSuspend] = useState(false);
  const [confirmRegen, setConfirmRegen] = useState(false);
  const [regenerated, setRegenerated] = useState<AdminRegenerateKeyResponse | null>(null);

  const patchMutation = useMutation({
    mutationFn: (patch: Record<string, unknown>) =>
      fetcher(`/admin/api/tenants/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'tenants', id] }),
  });

  const regenMutation = useMutation({
    mutationFn: () =>
      fetcher<AdminRegenerateKeyResponse>(
        `/admin/api/tenants/${id}/api-keys/regenerate`,
        { method: 'POST', body: '{}' },
      ),
    onSuccess: (result) => {
      setRegenerated(result);
      qc.invalidateQueries({ queryKey: ['admin', 'tenants', id] });
    },
  });

  if (isLoading) return <div>Loading…</div>;
  if (error) return <div className="card text-red-600">{(error as Error).message}</div>;
  if (!data) return null;

  const tenant = data.tenant;
  const isSuspended = tenant.status === 'suspended';

  return (
    <div className="space-y-4">
      <div>
        <Link to="/tenants" className="text-sm text-slate-600 hover:underline">
          ← Back to tenants
        </Link>
      </div>
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">{tenant.name ?? '(unnamed)'}</h1>
        <TenantStatusBadge status={tenant.status} />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <section className="card space-y-2">
          <h2 className="font-medium">Identity</h2>
          <div className="text-sm text-slate-700">
            <div><span className="text-slate-500">Slug:</span> {tenant.slug}</div>
            <div><span className="text-slate-500">ID:</span> <code className="text-xs">{tenant.id}</code></div>
            <div><span className="text-slate-500">Sandbox:</span> {tenant.is_sandbox ? 'yes' : 'no'}</div>
            <div><span className="text-slate-500">Custom domain:</span> {tenant.custom_domain ?? '—'} {tenant.custom_domain ? `(${tenant.custom_domain_status})` : ''}</div>
            <div><span className="text-slate-500">Created:</span> {formatDate(tenant.created_at)}</div>
          </div>
        </section>

        <section className="card space-y-2">
          <h2 className="font-medium">Counts</h2>
          <div className="text-sm">
            <div>Contracts: {data.counts.contracts}</div>
            <div>Users: {data.counts.users}</div>
            <div>API keys (active): {data.counts.api_keys_active}</div>
          </div>
        </section>

        <section className="card space-y-2">
          <h2 className="font-medium">Edit name</h2>
          <input
            className="input"
            placeholder={tenant.name ?? ''}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <button
            type="button"
            className="btn-primary"
            disabled={!name.trim() || patchMutation.isPending}
            onClick={() => patchMutation.mutate({ name: name.trim() })}
          >
            Save name
          </button>
        </section>

        <section className="card space-y-2">
          <h2 className="font-medium">Quota (contracts per day)</h2>
          <input
            type="number"
            className="input"
            placeholder={String(tenant.quota_contracts_per_day)}
            value={quota}
            onChange={(e) => setQuota(e.target.value)}
          />
          <button
            type="button"
            className="btn-primary"
            disabled={!quota || patchMutation.isPending}
            onClick={() =>
              patchMutation.mutate({
                quota_contracts_per_day: Number.parseInt(quota, 10),
              })
            }
          >
            Save quota
          </button>
        </section>

        <section className="card space-y-2">
          <h2 className="font-medium">Suspension</h2>
          <p className="text-sm text-slate-600">
            Suspended accounts return 423 from tenant write APIs. Existing
            signing links and tenant GETs continue working.
          </p>
          {isSuspended ? (
            <button
              type="button"
              className="btn-primary"
              onClick={() => patchMutation.mutate({ suspended: false })}
            >
              Unsuspend
            </button>
          ) : (
            <button
              type="button"
              className="btn-danger"
              onClick={() => setConfirmSuspend(true)}
            >
              Suspend
            </button>
          )}
        </section>

        <section className="card space-y-2">
          <h2 className="font-medium">API keys</h2>
          <p className="text-sm text-slate-600">
            Regenerating issues a new owner key and revokes all existing
            keys for this tenant. The new key is shown once.
          </p>
          <button
            type="button"
            className="btn-danger"
            onClick={() => setConfirmRegen(true)}
          >
            Regenerate API key
          </button>
          {regenerated ? (
            <div className="space-y-1 rounded-md border border-amber-300 bg-amber-50 p-2 text-sm">
              <div className="font-medium text-amber-800">
                New key (shown once):
              </div>
              <MaskedSecret value={regenerated.key} label="key:" />
              <div className="text-xs text-slate-600">
                id: <code>{regenerated.id}</code>
              </div>
            </div>
          ) : null}
        </section>
      </div>

      <ConfirmModal
        open={confirmSuspend}
        title="Suspend tenant?"
        description="Tenant write APIs will return 423 until you unsuspend. Existing signing links keep working."
        confirmLabel="Suspend"
        variant="danger"
        onCancel={() => setConfirmSuspend(false)}
        onConfirm={async () => {
          await patchMutation.mutateAsync({ suspended: true });
          setConfirmSuspend(false);
        }}
      />
      <ConfirmModal
        open={confirmRegen}
        title="Regenerate API key?"
        description="All current API keys for this tenant will be revoked. A new key will be issued and shown once."
        confirmLabel="Regenerate"
        variant="danger"
        onCancel={() => setConfirmRegen(false)}
        onConfirm={async () => {
          await regenMutation.mutateAsync();
          setConfirmRegen(false);
        }}
      />
    </div>
  );
}
